import React, { useState } from "react";
import { giroTechLogo, giroTechPlatformName } from "../utils/constants";

// Determina a URL base da API
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  const envUrl = window.REACT_APP_BACKEND_URL;
  if (!envUrl || envUrl === "") {
    return "/api";
  }

  if (envUrl.endsWith("/api")) {
    return envUrl;
  }

  return envUrl + "/api";
};

const API_BASE_URL = getApiBaseUrl();

const CertificateVerificationPage = () => {
  const [certificateId, setCertificateId] = useState("");
  const [loading, setLoading] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [error, setError] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!certificateId.trim()) return;

    setLoading(true);
    setError(null);
    setCertificateData(null);

    try {
      const response = await fetch(`${API_BASE_URL}/certificates/${certificateId.trim()}`);
      
      if (response.ok) {
        const data = await response.json();
        setCertificateData(data);
      } else if (response.status === 404) {
        setError("Certificado não encontrado ou não foi emitido pela Giro.Tech");
      } else {
        setError("Erro ao verificar certificado. Tente novamente.");
      }
    } catch (error) {
      console.error("Error verifying certificate:", error);
      setError("Erro ao verificar certificado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewVerification = () => {
    setCertificateId("");
    setCertificateData(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center">
            <img src={giroTechLogo} alt="Giro.Tech" className="h-12 w-auto mr-4" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{giroTechPlatformName}</h1>
              <p className="text-gray-600 text-sm">Verificação de Certificados</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold mb-2">Verificação de Certificado</h2>
            <p className="text-lg opacity-90">
              Digite o ID do certificado para verificar sua autenticidade
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {!certificateData ? (
              <form onSubmit={handleVerify} className="space-y-6">
                <div>
                  <label htmlFor="certificateId" className="block text-sm font-medium text-gray-700 mb-2">
                    ID do Certificado ou ID de Validação
                  </label>
                  <input
                    type="text"
                    id="certificateId"
                    value={certificateId}
                    onChange={(e) => setCertificateId(e.target.value)}
                    placeholder="Digite o ID do certificado ou ID de validação (ex: debug-cert-123 ou GIRO-ABC12345-2024)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Você pode usar tanto o ID do certificado quanto o ID de validação para verificar
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || !certificateId.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Verificando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Verificar Certificado</span>
                    </>
                  )}
                </button>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  </div>
                )}
              </form>
            ) : (
              <div className="space-y-6">
                {/* Success Message */}
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Certificado válido e autêntico!
                  </div>
                </div>

                {/* Certificate Details */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalhes do Certificado</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-700">ID do Certificado:</span>
                      <p className="text-gray-900 font-mono text-sm">{certificateData.id}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">ID de Validação:</span>
                      <p className="text-gray-900 font-mono text-sm">{certificateData.validation_id}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Nome do Aluno:</span>
                      <p className="text-gray-900">{certificateData.user_name || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Curso:</span>
                      <p className="text-gray-900">{certificateData.course_title || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Pontuação:</span>
                      <p className="text-gray-900">{certificateData.score}%</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Data de Conclusão:</span>
                      <p className="text-gray-900">
                        {new Date(certificateData.generated_at).toLocaleDateString("pt-BR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Aprovado
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-4">
                  <button
                    onClick={handleNewVerification}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Verificar Outro Certificado
                  </button>
                  <a
                    href="/"
                    className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg font-medium transition-colors text-center"
                  >
                    Ir para Plataforma
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 text-center">
            <div className="text-sm text-gray-500">
              Sistema de verificação de certificados da Giro.Tech
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerificationPage;
